import { Section } from '@/components/primitives/Section';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { motion } from 'framer-motion';
import { Zap, Home, Users, Globe, MessageCircle, Shield, DollarSign, Clock, Star, TrendingUp, CheckCircle2 } from 'lucide-react';
import { SEO, SEO_CONFIG } from '@/components/primitives/SEO';

export function FAQPage() {
  const categories = [
    {
      title: 'For Homeowners',
      icon: <Home className="w-6 h-6" strokeWidth={2.5} />,
      color: 'from-brand-500 to-brand-600',
      faqs: [
        {
          q: 'How fast can you clear my driveway?',
          a: 'During regular snowfall, most driveways are cleared within 2-4 hours of booking. Storm Pass members get priority matching and faster service during heavy storms.',
          icon: <Clock className="w-5 h-5" />,
        },
        {
          q: 'Do I need a seasonal contract?',
          a: 'Nope. SNOWBLOWR is 100% pay-per-job. Book when you need it, skip when you don\'t. Or upgrade to Storm Pass for automatic clearing all season long.',
          icon: <CheckCircle2 className="w-5 h-5" />,
        },
        {
          q: 'What if the operator misses a spot or does poor work?',
          a: 'Every job requires before/after photos. If you\'re not satisfied, you can dispute the completion within 2 hours and we\'ll resolve it—including partial refunds or sending another operator.',
          icon: <Shield className="w-5 h-5" />,
        },
        {
          q: 'How is pricing calculated?',
          a: 'Price = (base rate by driveway size × snow depth factor × time window factor + add-ons) × surge factor. You see the full breakdown before confirming your booking. No hidden fees.',
          icon: <DollarSign className="w-5 h-5" />,
        },
        {
          q: 'Can I schedule a clear in advance?',
          a: 'Yes! You can book for a specific time window (e.g., "tomorrow between 7-9am") or use ASAP for clearing within 2-4 hours.',
          icon: <Clock className="w-5 h-5" />,
        },
        {
          q: 'What payment methods do you accept?',
          a: 'We accept all major credit cards and debit cards via Stripe. Your card is pre-authorized when you book, but only charged after you approve the completed work.',
          icon: <DollarSign className="w-5 h-5" />,
        },
      ],
    },
    {
      title: 'For Operators',
      icon: <Users className="w-6 h-6" strokeWidth={2.5} />,
      color: 'from-accent-500 to-accent-600',
      faqs: [
        {
          q: 'How much do operators earn?',
          a: 'Operators keep 75-85% of each job, depending on tier and market. Average earnings are $40-80 per driveway, with top operators making $500-2000/week during storm weeks.',
          icon: <TrendingUp className="w-5 h-5" />,
        },
        {
          q: 'When do I get paid?',
          a: 'Automatic weekly payouts every Monday via Stripe Connect. Need it faster? Instant payout is available anytime for a 1.5% fee.',
          icon: <DollarSign className="w-5 h-5" />,
        },
        {
          q: 'What equipment do I need?',
          a: 'Neighbour tier: shovel or single-stage snowblower. Pro tier: two-stage snowblower or plow truck. You also need a smartphone and valid ID.',
          icon: <CheckCircle2 className="w-5 h-5" />,
        },
        {
          q: 'Can I decline job offers?',
          a: 'Yes. Every job offer includes distance, payout, and driveway details. You have 45 seconds to accept or decline. Declining doesn\'t hurt your standing.',
          icon: <CheckCircle2 className="w-5 h-5" />,
        },
        {
          q: 'Do I need insurance?',
          a: 'Pro tier operators must provide proof of liability insurance. Neighbour tier operators are covered under platform insurance for basic jobs.',
          icon: <Shield className="w-5 h-5" />,
        },
        {
          q: 'How does the matching algorithm work?',
          a: 'We consider distance, rating, accept rate, and current load. Top-rated operators within your service radius get first priority on job offers.',
          icon: <Star className="w-5 h-5" />,
        },
      ],
    },
    {
      title: 'General',
      icon: <Globe className="w-6 h-6" strokeWidth={2.5} />,
      color: 'from-brand-600 to-brand-700',
      faqs: [
        {
          q: 'Which cities do you serve?',
          a: 'We\'re live in Ottawa and Montreal, with Toronto, Calgary, and Edmonton launching winter 2025. Join our waitlist to get notified when we reach your city.',
          icon: <Globe className="w-5 h-5" />,
        },
        {
          q: 'Is there a Storm Pass in my city?',
          a: 'Storm Pass is rolling out city by city. Check /storm-pass to see availability and join the waitlist.',
          icon: <Zap className="w-5 h-5" />,
        },
        {
          q: 'How do you handle disputes?',
          a: 'Disputes are reviewed by our support team within 24 hours. We look at photos, GPS data, chat logs, and both parties\' accounts before making a decision.',
          icon: <Shield className="w-5 h-5" />,
        },
        {
          q: 'Is my payment information secure?',
          a: 'Yes. We use Stripe for all payment processing. We never store your card number—everything is tokenized and encrypted at bank-level security.',
          icon: <Shield className="w-5 h-5" />,
        },
        {
          q: 'Can I use SNOWBLOWR for commercial properties?',
          a: 'Yes! Pro tier operators can accept commercial jobs like office buildings, apartment complex walkways, and retail parking lots.',
          icon: <CheckCircle2 className="w-5 h-5" />,
        },
      ],
    },
  ];

  const quickStats = [
    { label: 'Avg Service Time', value: '< 2 hours', icon: <Clock className="w-5 h-5" /> },
    { label: 'Resolution Rate', value: '98%', icon: <CheckCircle2 className="w-5 h-5" /> },
    { label: 'Support Availability', value: '24/7', icon: <MessageCircle className="w-5 h-5" /> },
  ];

  return (
    <>
      <SEO {...SEO_CONFIG.faq} />
      {/* Hero */}
      <section className="relative bg-gradient-to-br from-brand-900 via-brand-800 to-brand-700 text-white overflow-hidden py-20 md:py-28">
        {/* Animated Background Grid */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff0f_1px,transparent_1px),linear-gradient(to_bottom,#ffffff0f_1px,transparent_1px)] bg-[size:4rem_4rem]" />
        </div>

        {/* Gradient Orbs */}
        <motion.div 
          className="absolute top-0 right-0 w-[500px] h-[500px] bg-brand-500/30 rounded-full blur-[120px]"
          animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div 
          className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-accent-500/20 rounded-full blur-[100px]"
          animate={{ scale: [1, 1.3, 1], opacity: [0.2, 0.4, 0.2] }}
          transition={{ duration: 10, repeat: Infinity, ease: "easeInOut", delay: 1 }}
        />

        <div className="relative container-default mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-accent-500/20 backdrop-blur-sm border border-accent-500/30 rounded-full mb-6"
          >
            <MessageCircle className="w-4 h-4 text-accent-500" fill="currentColor" />
            <span className="text-sm font-bold text-accent-500 uppercase tracking-wider">Got Questions?</span>
          </motion.div>

          <motion.h1 
            className="text-4xl sm:text-5xl md:text-6xl font-black mb-6 leading-tight"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            Everything you need to know
          </motion.h1>

          <motion.p 
            className="text-lg sm:text-xl text-snow-100 max-w-2xl mx-auto mb-12"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            Can't find what you're looking for? <span className="text-white font-semibold">Contact our support team</span> — we're here to help.
          </motion.p>

          {/* Quick Stats */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-3xl mx-auto"
          >
            {quickStats.map((stat, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: 0.4 + idx * 0.1 }}
              >
                <Card className="bg-white/10 backdrop-blur-sm border-white/20 p-6 hover:bg-white/15 transition-all duration-300">
                  <div className="flex flex-col items-center gap-2">
                    <div className="text-accent-500">
                      {stat.icon}
                    </div>
                    <div className="text-3xl font-black text-white">{stat.value}</div>
                    <div className="text-sm text-snow-200 font-medium">{stat.label}</div>
                  </div>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* FAQ Categories */}
      {categories.map((category, idx) => (
        <Section
          key={idx}
          title={category.title}
          background={idx % 2 === 0 ? 'default' : 'frost'}
        >
          <div className="max-w-4xl mx-auto">
            {/* Category Header Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="mb-8"
            >
              <Card className="p-6 bg-gradient-to-r from-white to-neutral-50 border-2 border-neutral-200 hover:border-brand-500 transition-colors duration-300">
                <div className="flex items-center gap-4">
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${category.color} text-white flex items-center justify-center shadow-lg`}>
                    {category.icon}
                  </div>
                  <div>
                    <h3 className="text-2xl font-black text-neutral-900">{category.title}</h3>
                    <p className="text-sm text-neutral-600 font-medium">{category.faqs.length} questions answered</p>
                  </div>
                </div>
              </Card>
            </motion.div>

            {/* FAQ Accordion */}
            <Accordion type="single" collapsible className="space-y-4">
              {category.faqs.map((faq, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.05 }}
                >
                  <AccordionItem value={`${idx}-${i}`} className="border-0 bg-white rounded-2xl shadow-sm hover:shadow-md transition-all duration-300">
                    <AccordionTrigger className="text-left font-bold text-neutral-900 hover:text-brand-500 px-6 py-5 hover:no-underline rounded-2xl">
                      <div className="flex items-start gap-3 w-full pr-4">
                        <div className={`mt-0.5 text-brand-500 flex-shrink-0`}>
                          {faq.icon}
                        </div>
                        <span className="text-base sm:text-lg">{faq.q}</span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="text-neutral-700 px-6 pb-5 pl-14">
                      <div className="text-base leading-relaxed font-medium bg-neutral-50 rounded-xl p-4 border-l-4 border-brand-500">
                        {faq.a}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                </motion.div>
              ))}
            </Accordion>
          </div>
        </Section>
      ))}

      {/* Contact CTA */}
      <section className="relative bg-gradient-to-br from-brand-900 via-brand-800 to-brand-700 text-white py-20 overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff0f_1px,transparent_1px),linear-gradient(to_bottom,#ffffff0f_1px,transparent_1px)] bg-[size:4rem_4rem]" />
        </div>

        <motion.div 
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-accent-500/20 rounded-full blur-[120px]"
          animate={{ scale: [1, 1.2, 1] }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
        />

        <div className="relative container-default mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-accent-500/20 backdrop-blur-sm border border-accent-500/30 rounded-full mb-6">
              <MessageCircle className="w-4 h-4 text-accent-500" />
              <span className="text-sm font-bold text-accent-500 uppercase tracking-wider">We're Here</span>
            </div>

            <h2 className="text-3xl sm:text-4xl md:text-5xl font-black mb-6">Still have questions?</h2>
            <p className="text-lg sm:text-xl text-snow-100 mb-8 max-w-2xl mx-auto font-medium">
              Our support team is standing by, ready to help. <strong className="text-white">Seriously—just ask.</strong>
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Button 
                variant="primary" 
                size="lg" 
                className="group bg-accent-500 hover:bg-accent-600 text-white border-0 shadow-glow-green font-black text-base px-8 py-6"
                onClick={() => window.location.href = 'mailto:hello@snowblowr.com'}
              >
                <MessageCircle className="w-5 h-5 mr-2" />
                Email Support
                <motion.span
                  animate={{ x: [0, 4, 0] }}
                  transition={{ duration: 1.5, repeat: Infinity }}
                  className="ml-2"
                >
                  →
                </motion.span>
              </Button>
              <div className="text-snow-100 text-sm font-medium">
                or email us at <strong className="text-white">hello@snowblowr.com</strong>
              </div>
            </div>

            {/* Support Hours */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="mt-12 pt-12 border-t border-white/20"
            >
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-3xl mx-auto">
                <div className="flex flex-col items-center gap-2">
                  <Clock className="w-6 h-6 text-accent-500" />
                  <div className="text-2xl font-black">24/7</div>
                  <div className="text-sm text-snow-200">Support Available</div>
                </div>
                <div className="flex flex-col items-center gap-2">
                  <MessageCircle className="w-6 h-6 text-accent-500" />
                  <div className="text-2xl font-black">&lt; 2hr</div>
                  <div className="text-sm text-snow-200">Avg Service Time</div>
                </div>
                <div className="flex flex-col items-center gap-2">
                  <Star className="w-6 h-6 text-accent-500" fill="currentColor" />
                  <div className="text-2xl font-black">4.9/5</div>
                  <div className="text-sm text-snow-200">Support Rating</div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </>
  );
}

